import { motion, AnimatePresence } from "framer-motion";
import { Bot } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAITutorStore } from "@/stores/ai-tutor-store";
import { cn } from "@/lib/cn";

/**
 * Floating AI Tutor button -- mirrors the v1 chat-fab from
 * frontend/public/chat-widget.js but in the Pulse design language.
 *
 * Placement: bottom-right of the viewport, lifted above the iOS home
 * indicator via env(safe-area-inset-bottom). Hides itself when the
 * sheet is open so the close button there is the only escape hatch
 * (avoids confusing dual-FAB chrome).
 *
 * Designed for the StudyShell where there's no bottom-nav; if you mount
 * this elsewhere with a bottom-nav, pass `bottomOffset="nav"` so it
 * clears the 64px nav bar.
 */
export function FloatingAITutorButton({
  bottomOffset = "default",
}: {
  bottomOffset?: "default" | "nav";
}) {
  const open       = useAITutorStore((s) => s.open);
  const isOpen     = useAITutorStore((s) => s.isOpen);
  const hasRef     = useAITutorStore((s) => !!s.pendingReference);
  const { chapterId } = useParams<{ chapterId: string }>();

  return (
    <AnimatePresence>
      {!isOpen ? (
        <motion.button
          key="ai-fab"
          type="button"
          onClick={() => open({ chapterId: chapterId ?? null })}
          aria-label="Open AI Tutor"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
          whileTap={{ scale: 0.94 }}
          className={cn(
            "fixed right-4 sm:right-6 z-50",
            "grid place-items-center h-14 w-14 rounded-full",
            "bg-brand text-white shadow-lg",
            "hover:scale-[1.04] transition-transform",
            "ring-1 ring-brand/30",
            bottomOffset === "nav"
              ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
              : "bottom-[calc(1rem+env(safe-area-inset-bottom))]",
          )}
          style={{
            boxShadow:
              "0 10px 24px rgba(249, 115, 22, 0.34), 0 4px 10px rgba(249, 115, 22, 0.18)",
          }}
        >
          <Bot className="h-6 w-6" />
          {hasRef ? (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-white border-2 border-brand" />
          ) : null}
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}
